import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { CalendarIcon, Search } from 'lucide-react';
import { getDateFnsLocale } from '@/lib/utils/date';
import { useAuth } from '@/features/auth';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { UniversalCombobox } from '@/components/common/UniversalCombobox/UniversalCombobox';
import { CollapsibleFilters, FilterField } from '@/components/common/CollapsibleFilters';
import type { TaskFilters } from '../types';

interface TasksFilterBarProps {
  filters: TaskFilters;
  searchValue: string;
  onSearchChange: (value: string) => void;
  idValue: string;
  onIdChange: (value: string) => void;
  updateFilter: <K extends keyof TaskFilters>(key: K, value: TaskFilters[K], resetPage?: boolean) => void;
  updateFilters: (updates: Partial<TaskFilters>, resetPage?: boolean) => void;
  activeFilterCount: number;
  onClearFilters: () => void;
  defaultFiltersExpanded?: boolean;
  hideAssignedTo?: boolean;
  open?: boolean;
}

export function TasksFilterBar({
  filters,
  searchValue,
  onSearchChange,
  idValue,
  onIdChange,
  updateFilter,
  updateFilters,
  activeFilterCount,
  onClearFilters,
  defaultFiltersExpanded = true,
  hideAssignedTo = false,
  open,
}: TasksFilterBarProps) {
  const { t, i18n } = useTranslation();
  const dateFnsLocale = getDateFnsLocale(i18n.language);
  const { hasAccess } = useAuth();
  // Filtering the board by assignee only makes sense for users who may see other
  // people's tasks (`task:get:all`). Everyone else is scoped to their own tasks
  // server-side, so the filter stays hidden for them.
  const canSeeAssignedTo = hasAccess('task', 'get:all');

  const {
    'createdAt.min': minDate,
    'createdAt.max': maxDate,
    'dueAt.min': dueAtMin,
    'dueAt.max': dueAtMax,
  } = filters;

  return (
    <CollapsibleFilters
      open={open}
      activeFilterCount={activeFilterCount}
      onClearFilters={onClearFilters}
      gridCols={5}
      defaultExpanded={defaultFiltersExpanded}
    >
      {/* Search */}
      <FilterField label={t('search')}>
        <div className="relative">
          <Input
            placeholder={t('search')}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10 h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
          />
          <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </FilterField>

      {/* Task ID (exact match) */}
      <FilterField label={t('taskId')}>
        <Input
          inputMode="numeric"
          placeholder={t('id')}
          value={idValue}
          onChange={(e) => onIdChange(e.target.value.replace(/\D/g, ''))}
          className="h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
        />
      </FilterField>

      {/* Matter */}
      <FilterField label={t('matter')}>
        <UniversalCombobox
          url="/matters"
          listUrl="/matters/select-box"
          labelKey="name"
          valueKey="id"
          selectedId={filters.matterId}
          onSelectItem={(matter) => updateFilter('matterId', matter ? (matter.id as number) : undefined)}
          placeholder={t('all')}
          className="w-full"
        />
      </FilterField>

      {/* Assigned To */}
      {!hideAssignedTo && canSeeAssignedTo && (
        <FilterField label={t('assignedTo')}>
          <UniversalCombobox
            url="/users"
            listUrl="/users/select-box"
            labelKey="name"
            valueKey="id"
            selectedId={filters.assignedStaffId}
            onSelectItem={(user) => updateFilter('assignedStaffId', user ? (user.id as number) : undefined)}
            placeholder={t('all')}
            className="w-full"
          />
        </FilterField>
      )}

      {/* Task Type */}
      <FilterField label={t('taskType')}>
        <UniversalCombobox
          url="/task-types"
          labelKey="name"
          valueKey="id"
          selectedId={filters.typeId}
          onSelectItem={(taskType) => {
            updateFilter('typeId', taskType ? (taskType.id as number) : undefined, true);
          }}
          placeholder={t('all')}
          className="w-full"
        />
      </FilterField>

      {/* Due Date Range */}
      <FilterField label={t('dueDateRange')}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            >
              <span className="truncate">
                {dueAtMin && dueAtMax
                  ? `${format(new Date(dueAtMin), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(dueAtMax), 'MM/dd/yy', { locale: dateFnsLocale })}`
                  : t('all')}
              </span>
              <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="range"
              selected={{
                from: dueAtMin ? new Date(dueAtMin) : undefined,
                to: dueAtMax ? new Date(dueAtMax) : undefined,
              }}
              onSelect={(range) => {
                updateFilters({
                  'dueAt.min': range?.from?.toISOString(),
                  'dueAt.max': range?.to?.toISOString(),
                });
              }}
              numberOfMonths={2}
              captionLayout="dropdown"
            />
          </PopoverContent>
        </Popover>
      </FilterField>

      {/* Created Date Range */}
      <FilterField label={t('createdDateRange')}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between font-normal h-10 bg-white border-[#cbd5e1] hover:border-[#94a3b8]"
            >
              <span className="truncate">
                {minDate && maxDate
                  ? `${format(new Date(minDate), 'MM/dd/yy', { locale: dateFnsLocale })} - ${format(new Date(maxDate), 'MM/dd/yy', { locale: dateFnsLocale })}`
                  : t('all')}
              </span>
              <CalendarIcon className="h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            <Calendar
              mode="range"
              selected={{
                from: minDate ? new Date(minDate) : undefined,
                to: maxDate ? new Date(maxDate) : undefined,
              }}
              onSelect={(range) => {
                updateFilters({
                  'createdAt.min': range?.from?.toISOString(),
                  'createdAt.max': range?.to?.toISOString(),
                });
              }}
              numberOfMonths={2}
              captionLayout="dropdown"
            />
          </PopoverContent>
        </Popover>
      </FilterField>

      {/* Created By */}
      <FilterField label={t('createdBy')}>
        <UniversalCombobox
          url="/users"
          listUrl="/users/select-box"
          labelKey="name"
          valueKey="id"
          selectedId={filters.createdById}
          onSelectItem={(user) => updateFilter('createdById', user ? (user.id as number) : undefined)}
          placeholder={t('all')}
          className="w-full"
        />
      </FilterField>
    </CollapsibleFilters>
  );
}
